import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { areEqual, deepCopy, DwComponent, DwComponentType, DwConfigService, DwContextService, DwEventService, DwExpressionService, DwExpressionStateService, DwFormContextMode, DwFormSectionComponent, DwMetaDataFormApi, DwMetaDataFormSectionStateService, DwMetaDataFormStateService, DwMetadataFormValidationBuilderService, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwSectionBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import {
  AcsWrittenResultEntity,
  AcsWrittenTestStatusAllItems,
  AppMetaDataItemNames,
  CourseEndorsementStudentEntity,
  StudentCourseForContextQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import { StudentCourseEndorsementContext } from '../../../courses/contexts/student-course-endorsement-context.service';
import { StudentRepositoryService } from '../../services/student-repository.service';
import { ACSSubmissionUtilService } from '../../services/acs-submission-util.service';
import { DwPubSubEventTopics } from '../../../z-devware/pubsub/models/dw-pub-sub-event-topics.model';
import { of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';

interface AcsWrittenEmptyStateViewModel {
  title: string;
  message: string;
  buttonText: string;
  processing: boolean;
}


@DwComponent({
  key: 'courseAcsWrittenResultsAdmin',
  name: 'Course ACS Written Results - Admin View',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.CourseEndorsementStudent,
  componentType: DwComponentType.formSection
})
@Component({
  selector: 'app-course-acs-written-results-admin-view',
  templateUrl: './course-acs-written-results-admin-view.component.html',
  styleUrls: ['./course-acs-written-results-admin-view.component.scss']
})
export class CourseAcsWrittenResultsAdminViewComponent extends DwSectionBaseComponent {
  acsWrittenResults: AcsWrittenResultEntity[];
  formApi: DwMetaDataFormApi;
  item = AppMetaDataItemNames.AcsWrittenResult;
  formMode = DwFormContextMode.edit;

  selectedAcsWrittenResult: AcsWrittenResultEntity;

  hideSelectedResult = false;
  courseStudentEndorsement: CourseEndorsementStudentEntity;
  studentCourseContext: StudentCourseForContextQueryEntity;
  isUploadingAcsResult = false;
  showProcessingMessage = false;
  lastResultFailed = false;
  acsEmptyState: AcsWrittenEmptyStateViewModel;
  private acsResultsLoadRequestId = 0;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private dwEventService: DwEventService,
    private studentRepositoryService: StudentRepositoryService,
    private acsSubmissionUtilService: ACSSubmissionUtilService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);

    this.formApi = this.dwFormStateService.state.formApi;

    this.dwFormStateService.state.formContext.contextMode = DwFormContextMode.view;
  }

  ngOnInit(): void {
    this.runAfterFormDataLoaded(() => {
      this.loadAcsWrittenResults();
      this.setupStatusRefreshEvents();
    });
  }

  onAcsWrittenResultChange(acsWrittenResult: AcsWrittenResultEntity): void {
    this.selectedAcsWrittenResult = acsWrittenResult;
    this.showProcessingMessage = acsWrittenResult?.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.New
      || acsWrittenResult?.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.Processing;
    this.updateEmptyState();

    this.hideSelectedResult = true;

    setTimeout(() => {
      this.hideSelectedResult = false;
    }, 100);
  }

  uploadAcsResults(files: FileList | null, fileControl: HTMLInputElement): void {
    const studentCourseId = this.courseStudentEndorsement?.StudentCourseId;
    const courseAcsTestId = this.studentCourseContext?.CourseAcsTestId;

    if (!files?.length || !studentCourseId || !this.studentCourseContext) {
      fileControl.value = '';
      return;
    }

    this.isUploadingAcsResult = true;
    this.lastResultFailed = false;
    this.updateEmptyState();

    const uploadSubscription = this.acsSubmissionUtilService.showSelectFilesModal(files, fileControl)
      .pipe(
        switchMap(media => media
          ? this.acsSubmissionUtilService.processACSTestFile(
            media,
            studentCourseId,
            courseAcsTestId
          )
          : of(null)
        ),
        finalize(() => {
          this.isUploadingAcsResult = false;
          fileControl.value = '';
          this.updateEmptyState();
        })
      )
      .subscribe(result => {
        if (result) {
          // Clear this before reloading. The reload recomputes the empty-state
          // view model during next(), while finalize runs after next completes.
          this.isUploadingAcsResult = false;
          this.lastResultFailed = result.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.FailedInvalid;
          this.updateEmptyState();
          this.loadAcsWrittenResults();
        }
      });

    this.subscriptions.push(uploadSubscription);
  }

  private loadAcsWrittenResults(): void {
    this.courseStudentEndorsement = this.formApi.getFormData<CourseEndorsementStudentEntity>();
    const studentCourseId = this.courseStudentEndorsement?.StudentCourseId;
    const loadRequestId = ++this.acsResultsLoadRequestId;

    if (!studentCourseId) {
      this.acsWrittenResults = [];
      this.selectedAcsWrittenResult = null;
      this.showProcessingMessage = false;
      this.updateEmptyState();
      return;
    }

    this.studentRepositoryService.getStudentCourseForContext(studentCourseId)
      .pipe(
        switchMap(context => this.studentRepositoryService.getAcsWrittenResults(studentCourseId)
          .pipe(map(results => ({ context, results }))))
      )
      .subscribe(({ context, results }) => {
        if (loadRequestId !== this.acsResultsLoadRequestId) {
          return;
        }

        this.studentCourseContext = context;
        this.acsWrittenResults = results || [];

        this.selectedAcsWrittenResult = this.acsWrittenResults.length > 0 ? this.acsWrittenResults[0] : null;
        this.showProcessingMessage = this.selectedAcsWrittenResult?.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.New
          || this.selectedAcsWrittenResult?.AcsWrittenTestStatusId === AcsWrittenTestStatusAllItems.Processing;
        this.updateEmptyState();
      });
  }

  private setupStatusRefreshEvents(): void {
    [
      AppMetaDataItemNames.AcsWrittenResult,
      AppMetaDataItemNames.StudentCourse
    ].forEach(itemName => {
      this.subscriptions.push(
        this.dwEventService
          .getEventData(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(itemName))
          .subscribe(() => this.loadAcsWrittenResults())
      );
    });

    this.subscriptions.push(
      this.dwEventService
        .getEventData(DwPubSubEventTopics.messageReceived(ACSSubmissionUtilService.ACS_WRITTEN_RESULT_TOPIC))
        .subscribe(() => this.loadAcsWrittenResults())
    );
  }

  private updateEmptyState(): void {
    const processing = this.isUploadingAcsResult || this.showProcessingMessage;

    if (processing) {
      this.acsEmptyState = {
        title: 'Processing ACS written results',
        message: 'Your ACS written test is being processed. Results will appear here when processing is complete.',
        buttonText: 'Processing results',
        processing: true
      };
      return;
    }

    if (this.lastResultFailed) {
      this.acsEmptyState = {
        title: 'ACS written processing failed',
        message: 'We could not process the uploaded file. Verify the document is clear and upload it again.',
        buttonText: 'Reupload results',
        processing: false
      };
      return;
    }

    this.acsEmptyState = {
      title: 'No ACS written results',
      message: 'No ACS written results have been uploaded yet.',
      buttonText: 'Upload results',
      processing: false
    };
  }
}
